/**
 * DeviceSettingsPanel - camera, microphone and speaker pickers for a call in progress.
 *
 * The single most common way a recovery bridge wastes its first two minutes is somebody
 * talking into the wrong microphone, or listening on a headset that is not the one they are
 * wearing. Chime already tracks the device lists and the current selection; nothing exposed
 * them, so the only fix was to leave the call, change the OS default, and come back - during
 * an incident, on the call where the decisions are being made.
 *
 * Everything here comes from the installed amazon-chime-sdk-component-library-react (3.12):
 * useAudioInputs / useVideoInputs / useAudioOutputs are provided by the DevicesProvider that
 * MeetingProvider already mounts, and the switches go through MeetingManager's
 * start*Device methods, which re-publish to the meeting. No new dependency.
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useAudioInputs,
    useAudioOutputs,
    useVideoInputs,
    useMeetingManager
} from 'amazon-chime-sdk-component-library-react';
import './VideoCall.css';

/**
 * Choosing the speaker needs HTMLMediaElement.setSinkId, which Firefox and Safari do not
 * implement. Offering a picker that silently does nothing is worse than not offering one, so
 * the panel says where the setting actually lives on those browsers instead.
 */
const CAN_CHOOSE_SPEAKER = typeof HTMLMediaElement !== 'undefined'
    && 'setSinkId' in HTMLMediaElement.prototype;

/**
 * Chime's current selection, as a device id we can match against an <option>.
 *
 * `AudioInputDevice` and `VideoInputDevice` are wider than "a device id": they legally hold a
 * plain id string, a MediaDeviceInfo-ish object, a MediaTrackConstraints, or a whole
 * MediaStream. A MediaStream has no device id at all, and the previous version of this
 * returned '' for it - which left the <select> with a value matching none of its options, so
 * the browser showed the FIRST device in the list as if it were the one in use. On a picker
 * whose entire purpose is telling somebody which microphone they are actually talking into,
 * silently naming the wrong one is the failure mode to avoid.
 *
 * Returns '' when there is nothing we can name; the caller shows that honestly.
 */
function selectedDeviceId(selection) {
    if (!selection) return '';
    if (typeof selection === 'string') return selection;

    const { deviceId } = selection;
    if (typeof deviceId === 'string') return deviceId;

    // MediaTrackConstraints: `{ deviceId: 'x' }` is covered above, `{ deviceId: { exact: 'x' } }`
    // and the array forms are not.
    if (deviceId && typeof deviceId === 'object') {
        const exact = Array.isArray(deviceId) ? deviceId[0] : deviceId.exact;
        if (typeof exact === 'string') return exact;
        if (Array.isArray(exact) && typeof exact[0] === 'string') return exact[0];
    }

    // A MediaStream, or anything else with no device id in it.
    return '';
}

function DeviceSelect({ id, label, devices, value, onChange, disabled, emptyLabel, unlistedLabel }) {
    // A <select> whose value matches no option displays the first option instead, which reads
    // as "this is your device". Only claim a device when it is genuinely in the list.
    const isListed = value !== '' && devices.some((device) => device.deviceId === value);

    return (
        <div className="device-field">
            <label className="device-label" htmlFor={id}>{label}</label>
            <select
                id={id}
                className="device-select"
                value={isListed ? value : ''}
                disabled={disabled || !devices.length}
                onChange={(event) => onChange(event.target.value)}
            >
                {devices.length === 0 && <option value="">{emptyLabel}</option>}
                {devices.length > 0 && !isListed && (
                    <option value="" disabled>{unlistedLabel}</option>
                )}
                {devices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                        {device.label || device.deviceId}
                    </option>
                ))}
            </select>
        </div>
    );
}

function DeviceSettingsPanel({ isOpen, onClose, callType }) {
    const { t } = useTranslation();
    const meetingManager = useMeetingManager();
    const audioInputs = useAudioInputs();
    const audioOutputs = useAudioOutputs();
    const videoInputs = useVideoInputs();

    const [error, setError] = useState(null);

    // A device can be gone by the time it is picked (unplugged headset, camera claimed by
    // another app). Chime rejects, and without this the select just snaps back with no reason
    // given - which reads as the picker being broken.
    const switchDevice = useCallback(async (apply) => {
        setError(null);
        try {
            await apply();
        } catch (err) {
            console.error('[VideoCall] Device switch failed:', err);
            setError(t('aiChat.calls.deviceSwitchFailed', {
                reason: (err && (err.message || err.name)) || t('aiChat.messages.unknown')
            }));
        }
    }, [t]);

    if (!isOpen) return null;

    return (
        <div className="device-settings-panel" role="dialog" aria-label={t('aiChat.calls.deviceSettings')}>
            <div className="participants-panel-header">
                <h3>{t('aiChat.calls.deviceSettings')}</h3>
                <button type="button" className="panel-close-btn" onClick={onClose} title={t('aiChat.modals.close')}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            <div className="device-settings-body">
                <DeviceSelect
                    id="call-device-mic"
                    label={t('aiChat.calls.microphone')}
                    devices={audioInputs.devices || []}
                    value={selectedDeviceId(audioInputs.selectedDevice)}
                    emptyLabel={t('aiChat.calls.noDevicesFound')}
                    unlistedLabel={t('aiChat.calls.deviceInUse')}
                    onChange={(deviceId) => switchDevice(() => meetingManager.startAudioInputDevice(deviceId))}
                />

                {CAN_CHOOSE_SPEAKER ? (
                    <DeviceSelect
                        id="call-device-speaker"
                        label={t('aiChat.calls.speaker')}
                        devices={audioOutputs.devices || []}
                        value={selectedDeviceId(audioOutputs.selectedDevice)}
                        emptyLabel={t('aiChat.calls.noDevicesFound')}
                        unlistedLabel={t('aiChat.calls.deviceInUse')}
                        onChange={(deviceId) => switchDevice(() => meetingManager.startAudioOutputDevice(deviceId))}
                    />
                ) : (
                    <p className="device-hint">{t('aiChat.calls.speakerSelectionUnsupported')}</p>
                )}

                {callType === 'video' && (
                    <DeviceSelect
                        id="call-device-camera"
                        label={t('aiChat.calls.camera')}
                        devices={videoInputs.devices || []}
                        value={selectedDeviceId(videoInputs.selectedDevice)}
                        emptyLabel={t('aiChat.calls.noDevicesFound')}
                        unlistedLabel={t('aiChat.calls.deviceInUse')}
                        onChange={(deviceId) => switchDevice(() => meetingManager.startVideoInputDevice(deviceId))}
                    />
                )}

                {error && <p className="device-error" role="alert">{error}</p>}
            </div>
        </div>
    );
}

export default DeviceSettingsPanel;
